// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"
import { createAutoSubmitter } from "./shared/auto_submit"

/**
 * Auto-submit Controller
 * @gem rapid_rails_ui
 * @version 0.86.0
 * @updated 2026-10-14
 *
 * Submits the field's form when its value changes, for filter forms that
 * update results without a submit button. Wired by `auto_submit:` on
 * rui_select, rui_radio_button, rui_input and rui_combobox (which listens
 * for the combobox's own change event).
 *
 * - Uses form.requestSubmit(), so form validation, Turbo Drive and Turbo
 *   Frame forms work as if the user pressed the submit button.
 * - `delay` (ms) waits until typing pauses (search boxes); 0 submits at once.
 * - The same value is never submitted twice in a row.
 * - Enter (submitNow) submits at once, also in forms without a submit
 *   button, and drops the pending delayed submit. Enter that confirms IME
 *   composition is ignored.
 * - A pending delayed submit is dropped when the form is submitted another
 *   way (a submit button), or while the user is composing text (IME).
 * - A submit refused by form validation is not remembered, so the same value
 *   can be tried again once the form is valid.
 * - Does nothing when the field is not inside a form.
 * - CSP-safe: no inline handlers.
 *
 * Values:
 *   delay - milliseconds to wait after the last change (default 0)
 *
 * Actions:
 *   submit    - call from `change` or `input`
 *   submitNow - call from `keydown.enter` on text inputs
 */
export default class extends Controller {
  static values = { delay: { type: Number, default: 0 } }

  connect() {
    this.form = this.element.form || this.element.closest("form")
    this.submitter = createAutoSubmitter({
      delay: this.delayValue,
      submit: () => this.requestSubmit()
    })
    this.onFormSubmit = () => {
      this.didSubmit = true
      this.submitter.submitted(this.lastKey)
    }
    this.form?.addEventListener("submit", this.onFormSubmit)
  }

  disconnect() {
    this.submitter?.cancel()
    this.form?.removeEventListener("submit", this.onFormSubmit)
  }

  submit(event) {
    if (!this.form) return
    if (event?.isComposing) return this.submitter.cancel()
    this.lastKey = this.keyFor(event)
    this.submitter.request(this.lastKey)
  }

  submitNow(event) {
    if (!this.form || event?.isComposing) return
    event?.preventDefault()
    this.submitter.cancel()
    this.lastKey = this.keyFor(event)
    this.requestSubmit()
  }

  // True when the form really submitted (requestSubmit fires "submit" only
  // after validation passes).
  requestSubmit() {
    if (typeof this.form.requestSubmit !== "function") {
      this.form.submit()
      return true
    }
    this.didSubmit = false
    this.form.requestSubmit()
    return this.didSubmit === true
  }

  // What was chosen, so the same choice is never submitted twice in a row.
  // Component events (e.g. combobox:change) carry their values in detail;
  // native events use the field's name plus value (or checked state).
  keyFor(event) {
    const values = event?.detail?.values
    if (Array.isArray(values)) return `${event.type}=${values.join(",")}`

    const field = event?.target || this.element
    let value = field.value
    if (field.type === "checkbox" || field.type === "radio") {
      value = `${field.value}:${field.checked}`
    } else if (field.multiple && field.selectedOptions) {
      value = Array.from(field.selectedOptions, (option) => option.value).join(",")
    }
    return `${field.name || ""}=${value ?? ""}`
  }
}
